import React, { useState, useEffect, useRef } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Dialog } from "primereact/dialog";
import { Calendar } from "primereact/calendar";
import { Toast } from "primereact/toast";
import { ProgressSpinner } from "primereact/progressspinner";
import RewardsService from "../../../services/rewards/rewards.service";
import editUserServices from "../../../services/edit-users/edit-users.service";
import Helpers from "../../../utils/helpers";
import TransactionDialog from "../details/TransactionDialog";
import {
  AdminDashboardMetrics,
  UserRewardSummary,
  UserReferral,
  AdminRewardTransaction,
} from "../../../models/rewards-model";
import { OtherUserModel } from "../../../models/all-user/edit-user-model";
import "./RewardsAdminDashboard.scss";

const transactionSortOptions: { id: string; label: string; sortField: string; sortOrder: 1 | -1 }[] = [
  { id: "date-newest", label: "Date (Newest First)", sortField: "transactionDate", sortOrder: -1 },
  { id: "date-oldest", label: "Date (Oldest First)", sortField: "transactionDate", sortOrder: 1 }
  // { id: "type-za", label: "Type (Z-A)", sortField: "transactionTypeName", sortOrder: -1 }
];

const REFERRAL_TREE_MIN_ZOOM = 50;
const REFERRAL_TREE_DEFAULT_ZOOM = 60;
const REFERRAL_TREE_MAX_ZOOM = 125;
const REFERRAL_TREE_ZOOM_STEP = 10;

type ReferralChartPerson = {
  userId?: number | null;
  name: string;
  phone?: string | null;
  role: "referrer" | "selected" | "referred";
  referralCount?: number;
  joinedDate?: string | null;
};

const RewardsAdminDashboard: React.FC = () => {
  const toast = useRef<Toast>(null);

  // Dashboard metrics
  const [metrics, setMetrics] = useState<AdminDashboardMetrics>({
    totalEarned: 0,
    totalAvailablePoints: 0,
    totalPointsRedeemed: 0,
    totalExpiredPoints: 0,
    rewardToCurrencyRatio: 0,
  });
  const [metricsLoading, setMetricsLoading] = useState(true);
  const [rewardToCurrencyRatio, setRewardToCurrencyRatio] = useState<number>(0);
  const today = new Date();
  const oneYearAgo = new Date();
  oneYearAgo.setFullYear(today.getFullYear() - 1);

  const [dateRange, setDateRange] = useState<Date[]>([
    oneYearAgo,
    today,
  ]);

  // Summary table
  const [summaryData, setSummaryData] = useState<UserRewardSummary[]>([]);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [first, setFirst] = useState(0);
  const [searchText, setSearchText] = useState("");
  const [sortField, setSortField] = useState("userName");
  const [sortOrder, setSortOrder] = useState<1 | -1>(1);

  // Referral dialog
  const [showReferralDialog, setShowReferralDialog] = useState(false);
  const [referrals, setReferrals] = useState<UserReferral[]>([]);
  const [referralLoading, setReferralLoading] = useState(false);
  const [selectedUserName, setSelectedUserName] = useState("");
  const [selectedReferralUser, setSelectedReferralUser] = useState<UserRewardSummary | null>(null);
  const [referrerDetails, setReferrerDetails] = useState<ReferralChartPerson | null>(null);
  const [referralTreeZoom, setReferralTreeZoom] = useState(REFERRAL_TREE_DEFAULT_ZOOM);

  // Transaction dialog
  const [showTransactionDialog, setShowTransactionDialog] = useState(false);
  const [transactions, setTransactions] = useState<AdminRewardTransaction[]>([]);
  const [transactionLoading, setTransactionLoading] = useState(false);
  const [transactionTotalRecords, setTransactionTotalRecords] = useState(0);
  const [transactionPageNumber, setTransactionPageNumber] = useState(1);
  const [transactionPageSize, setTransactionPageSize] = useState(10);
  const [transactionFirst, setTransactionFirst] = useState(0);
  const [selectedUserId, setSelectedUserId] = useState<number>(0);
  const [transactionUserName, setTransactionUserName] = useState("");
  const [txSearchInput, setTxSearchInput] = useState("");
  const [txSearchQuery, setTxSearchQuery] = useState<string | null>(null);
  const [transactionSortField, setTransactionSortField] = useState<string>("transactionDate");
  const [transactionSortOrder, setTransactionSortOrder] = useState<1 | -1>(-1);
  const calendarRef = useRef<Calendar>(null);
  const referralChartScrollerRef = useRef<HTMLDivElement>(null);

  // Find the exact object from the array so the Dropdown can 'highlight' it
  const selectedTxSortOption = transactionSortOptions.find(
    o => o.sortField === transactionSortField && o.sortOrder === transactionSortOrder
  ) || transactionSortOptions[0];
  const showMessage = (
    summary: string,
    detail: string,
    severity: "success" | "error" | "info" | "warn"
  ) => {
    toast.current?.show({ severity, summary, detail, life: 3000 });
  };

  const formatDateTimeForApi = (date: Date, isEndDate: boolean = false) => {
    const d = new Date(date);

    if (isEndDate) {
      d.setHours(23, 59, 59, 0);
    } else {
      d.setHours(0, 0, 0, 0);
    }

    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");

    const hours = String(d.getHours()).padStart(2, "0");
    const minutes = String(d.getMinutes()).padStart(2, "0");
    const seconds = String(d.getSeconds()).padStart(2, "0");

    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
  };

  // Fetch dashboard metrics
  const fetchMetrics = async (fromDateVal: Date, toDateVal: Date) => {
    setMetricsLoading(true);
    try {
      const formattedFrom = formatDateTimeForApi(fromDateVal, false);
      const formattedTo = formatDateTimeForApi(toDateVal, true);

      const data = await RewardsService.getAdminDashboardMetrics({
        fromDate: formattedFrom,
        toDate: formattedTo,
      });
      setMetrics(data);
      // Ratio comes directly from the dashboard API — no separate call needed
      if (data.rewardToCurrencyRatio) {
        setRewardToCurrencyRatio(data.rewardToCurrencyRatio);
      }
    } catch (error) {
      showMessage("Error", "Failed to load dashboard metrics", "error");
    } finally {
      setMetricsLoading(false);
    }
  };

  const fieldToApiMap: Record<string, string> = {
    userName: "name",
    referralCount: "RefferedCount",
    availablePoints: "TotalAvailable",
    totalEarnedPoints: "TotalEarned",
    totalRedeemedPoints: "TotalRedeemed",
    expiredPoints: "TotalExpired",
  };

  // Fetch summary table data
  const fetchSummary = async (
    page: number,
    size: number,
    search: string,
    field: string,
    order: 1 | -1,
    fromD?: Date,
    toD?: Date
  ) => {
    setSummaryLoading(true);
    try {
      const apiSortBy = fieldToApiMap[field] || field;
      const apiSortOrder = order === 1 ? "asc" : "desc";
      const fromToUse = fromD || dateRange[0];
      const toToUse = toD || dateRange[1];

      const payload: any = {
        searchText: search,
        sortBy: apiSortBy,
        sortOrder: apiSortOrder,
        pageNumber: page,
        pageSize: size,
      };

      if (fromToUse) payload.fromDate = formatDateTimeForApi(fromToUse, false);
      if (toToUse) payload.toDate = formatDateTimeForApi(toToUse, true);

      const response = await RewardsService.getUserRewardSummary(payload);
      setSummaryData(response.data);
      setTotalRecords(response.totalRecords);
    } catch (error) {
      showMessage("Error", "Failed to load user reward summary", "error");
    } finally {
      setSummaryLoading(false);
    }
  };

  const getUserProfile = async (userId: number): Promise<OtherUserModel | null> => {
    try {
      const response = await editUserServices.getUsers(`?$filter=true and userID eq ${userId}`);
      return Array.isArray(response) && response.length > 0 ? response[0] : null;
    } catch (error) {
      console.error("Error fetching user profile:", error);
      return null;
    }
  };

  const getFittedReferralZoom = (referralTotal: number) => {
    if (referralTotal <= 48) return REFERRAL_TREE_DEFAULT_ZOOM;
    return REFERRAL_TREE_MIN_ZOOM;
  };

  const getBoundedReferralZoom = (nextZoom: number) =>
    Math.min(
      REFERRAL_TREE_MAX_ZOOM,
      Math.max(REFERRAL_TREE_MIN_ZOOM, nextZoom)
    );

  const updateReferralTreeZoom = (nextZoom: number) => {
    setReferralTreeZoom(getBoundedReferralZoom(nextZoom));
  };

  const handleReferralTreeWheel = (event: React.WheelEvent<HTMLDivElement>) => {
    if (Math.abs(event.deltaY) < 2) return;

    event.preventDefault();

    const nextZoom = getBoundedReferralZoom(
      referralTreeZoom + (event.deltaY < 0 ? 5 : -5)
    );
    if (nextZoom === referralTreeZoom) return;

    const scroller = referralChartScrollerRef.current;
    if (!scroller) {
      setReferralTreeZoom(nextZoom);
      return;
    }

    const rect = scroller.getBoundingClientRect();
    const offsetX = event.clientX - rect.left;
    const offsetY = event.clientY - rect.top;
    const anchorX = scroller.scrollLeft + offsetX;
    const anchorY = scroller.scrollTop + offsetY;
    const zoomRatio = nextZoom / referralTreeZoom;

    setReferralTreeZoom(nextZoom);

    window.requestAnimationFrame(() => {
      scroller.scrollLeft = anchorX * zoomRatio - offsetX;
      scroller.scrollTop = anchorY * zoomRatio - offsetY;
    });
  };

  const closeReferralDialog = () => {
    setShowReferralDialog(false);
    setReferralTreeZoom(REFERRAL_TREE_DEFAULT_ZOOM);
  };

  // Fetch user referrals
  const fetchReferrals = async (user: UserRewardSummary) => {
    setReferralLoading(true);
    setSelectedUserName(user.userName);
    setSelectedReferralUser(user);
    setReferrerDetails(null);
    setReferralTreeZoom(getFittedReferralZoom(user.referralCount || 0));
    setShowReferralDialog(true);
    try {
      const [data, profile] = await Promise.all([
        RewardsService.getUserReferrals(user.userId),
        getUserProfile(user.userId),
      ]);

      let referrer: ReferralChartPerson | null = null;
      if (profile?.referredBy) {
        const referrerProfile = await getUserProfile(profile.referredBy);
        referrer = {
          userId: profile.referredBy,
          name: profile.referredByName || referrerProfile?.name || "Referrer",
          phone: referrerProfile?.primaryContactNbr || null,
          role: "referrer",
        };
      }

      setReferrals(data);
      setReferrerDetails(referrer);
      setReferralTreeZoom(getFittedReferralZoom(data.length));
    } catch (error) {
      showMessage("Error", "Failed to load referrals", "error");
    } finally {
      setReferralLoading(false);
    }
  };

  // Field → API sortBy key map for transaction columns
  // Field → API sortBy key map for transaction columns
  const txFieldToApiMap: Record<string, string> = {
    transactionTypeName: "TransactionType",
    points: "points",
    remainingPoints: "remainingpoints",
    transactionDate: "TransactionDate",
    expiryDate: "expirydate",
    orderId: "OrderID",
    referredUserName: "referreduser",
    id: "TransactionDate"
  };

  // Fetch user transactions
  const fetchTransactions = async (
    userId: number,
    userName: string,
    page: number = 1,
    size: number = 10,
    searchTxt: string | null = null,
    sortBy: string = "TransactionDate",
    sortOrder: 1 | -1 = -1
  ) => {
    setTransactionLoading(true);
    setSelectedUserId(userId);
    setTransactionUserName(userName);
    setShowTransactionDialog(true);
    try {
      const response = await RewardsService.getUserRewardTransactions({
        userId,
        pageNumber: page,
        pageSize: size,
        searchText: searchTxt ?? undefined,
        sortBy: sortBy, // Use the argument passed to the function
        sortOrder: sortOrder === 1 ? "ASC" : "DESC",
      });
      setTransactions(response.data);
      setTransactionTotalRecords(response.totalRecords);
      console.log(response.data);
    } catch (error) {
      showMessage("Error", "Failed to load transactions", "error");
    } finally {
      setTransactionLoading(false);
    }
  };

  useEffect(() => {
    fetchMetrics(dateRange[0], dateRange[1]);
    fetchSummary(pageNumber, pageSize, searchText, sortField, sortOrder);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handlers
  const onPageChange = (event: any) => {
    const newPage = Math.floor(event.first / event.rows) + 1;
    setFirst(event.first);
    setPageNumber(newPage);
    setPageSize(event.rows);
    fetchSummary(newPage, event.rows, searchText, sortField, sortOrder);
  };

  const onSort = (event: any) => {
    const newSortField = event.sortField;
    const newSortOrder = event.sortOrder as 1 | -1;
    setSortField(newSortField);
    setSortOrder(newSortOrder);
    setFirst(0);
    setPageNumber(1);
    fetchSummary(1, pageSize, searchText, newSortField, newSortOrder);
  };

  const onSearch = () => {
    setFirst(0);
    setPageNumber(1);
    fetchSummary(1, pageSize, searchText, sortField, sortOrder);
  };

  const onSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      onSearch();
    }
  };

  const onSearchClear = () => {
    setSearchText("");
    setFirst(0);
    setPageNumber(1);
    fetchSummary(1, pageSize, "", sortField, sortOrder);
  };

  const onDateRangeChange = (e: any) => {
    setDateRange(e.value);
    if (e.value && e.value[0] && e.value[1]) {
      fetchMetrics(e.value[0], e.value[1]);
      setFirst(0);
      setPageNumber(1);
      fetchSummary(1, pageSize, searchText, sortField, sortOrder, e.value[0], e.value[1]);
      // ✅ Close the calendar popup
      calendarRef.current?.hide();
    }
  };

  // Converts the DataTable column field to the API sortBy key
  const getApiSortField = (columnField: string) =>
    txFieldToApiMap[columnField] || columnField;

  const onTransactionPageChange = (event: any) => {
    const newPage = Math.floor(event.first / event.rows) + 1;
    setTransactionFirst(event.first);
    setTransactionPageNumber(newPage);
    setTransactionPageSize(event.rows);
    fetchTransactions(selectedUserId, transactionUserName, newPage, event.rows, txSearchQuery, getApiSortField(transactionSortField), transactionSortOrder);
  };

  const onTransactionSortChange = (val: any) => {
    if (!val) return;
    setTransactionSortField(val.sortField);
    setTransactionSortOrder(val.sortOrder);
    setTransactionFirst(0);
    setTransactionPageNumber(1);
    fetchTransactions(
      selectedUserId,
      transactionUserName,
      1,
      transactionPageSize,
      txSearchQuery,
      getApiSortField(val.sortField),
      val.sortOrder
    );
  };

  const onTxSearch = () => {
    const trimmed = txSearchInput.trim();
    if (!trimmed) {
      onTxSearchClear();
      return;
    }
    setTxSearchQuery(trimmed);
    setTransactionFirst(0);
    setTransactionPageNumber(1);
    fetchTransactions(selectedUserId, transactionUserName, 1, transactionPageSize, trimmed, getApiSortField(transactionSortField), transactionSortOrder);
  };

  const onTxSearchClear = () => {
    setTxSearchInput("");
    setTxSearchQuery(null);
    setTransactionFirst(0);
    setTransactionPageNumber(1);
    fetchTransactions(selectedUserId, transactionUserName, 1, transactionPageSize, null, getApiSortField(transactionSortField), transactionSortOrder);
  };

  // Format helpers
  const formatNumber = (num: number) => {
    return num.toLocaleString("en-IN");
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatDateTime = (dateString: string) => {
    if (!dateString) return '-';

    // Treat as UTC if no timezone specified
    const dateStr = (dateString.endsWith('Z') || /[+-]\d{2}:?\d{2}$/.test(dateString))
      ? dateString
      : `${dateString}Z`;

    return new Date(dateStr).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      timeZone: 'Asia/Kolkata'
    });
  };

  // Column templates
  const userNameTemplate = (rowData: UserRewardSummary) => (
    <Button
      label={rowData.userName}
      className="p-button-link p-0 rewardsUserTreeButton"
      onClick={() => fetchReferrals(rowData)}
      tooltip="View Referral Tree"
      tooltipOptions={{ position: "top" }}
    />
  );

  const referralCountTemplate = (rowData: UserRewardSummary) => {
    if (rowData.referralCount === 0) {
      return <span>0</span>;
    }
    return (
      <Button
        label={rowData.referralCount.toString()}
        className="p-button-link p-0"
        onClick={() => fetchReferrals(rowData)}
      />
    );
  };

  const actionsTemplate = (rowData: UserRewardSummary) => {
    return (
      <Button
        icon="pi pi-eye"
        className="p-button-rounded p-button-text p-button-sm"
        tooltip="View Transactions"
        tooltipOptions={{ position: "top" }}
        onClick={() => fetchTransactions(rowData.userId, rowData.userName)}
      />
    );
  };

  const pointsTemplate = (field: string) => (rowData: UserRewardSummary) => {
    const pts = rowData[field as keyof UserRewardSummary] as number;
    const ratio = rowData.rewardToCurrencyRatio || 0;
    return (
      <span style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {ratio > 0 ? (
          <>
            <span style={{ color: "#16a34a", fontWeight: "bold" }}>
              {Helpers.formatAmountInINR(Math.round(pts * ratio))}
            </span>
            <span style={{ fontSize: "0.85rem", color: "#6b7280" }}>
              {formatNumber(pts)} pts
            </span>
          </>
        ) : (
          <span>{formatNumber(pts)} pts</span>
        )}
      </span>
    );
  };

  // Referral dialog templates
  const referralDateTemplate = (rowData: UserReferral) => {
    return formatDateTime(rowData.joinedDate);
  };

  const renderReferralPersonCard = (person: ReferralChartPerson) => {
    const roleLabel: Record<ReferralChartPerson["role"], string> = {
      referrer: "Referred by",
      selected: "Selected user",
      referred: "Referred user",
    };

    return (
      <div className="rewardOrgNodeContent">
        <div className="rewardOrgAvatar">
          {person.name?.charAt(0)?.toUpperCase() || "U"}
        </div>
        <div className="rewardOrgName">{person.name || "-"}</div>
        <div className="rewardOrgPhone">{person.phone || "No phone"}</div>
        <span className={`rewardOrgRole rewardOrgRole-${person.role}`}>
          {roleLabel[person.role]}
        </span>
        {person.role === "selected" && (
          <div className="rewardOrgCount">
            <strong>{person.referralCount || 0}</strong>
            <span>referrals made</span>
          </div>
        )}
        {person.joinedDate && (
          <div className="rewardOrgJoined">Joined {formatDateTime(person.joinedDate)}</div>
        )}
      </div>
    );
  };

  const selectedReferralPerson: ReferralChartPerson | null = selectedReferralUser
    ? {
      userId: selectedReferralUser.userId,
      name: selectedReferralUser.userName,
      phone: selectedReferralUser.phone,
      role: "selected",
      referralCount: selectedReferralUser.referralCount || referrals.length,
    }
    : null;

  // Metric cards data
  const metricCards = [
    {
      label: "Total Points Earned",
      value: metrics.totalEarned,
      icon: "pi pi-arrow-up-right",
      className: "Credited",
    },
    {
      label: "Available Points",
      value: metrics.totalAvailablePoints,
      icon: "pi pi-wallet",
      className: "Available",
    },
    {
      label: "Points Redeemed",
      value: metrics.totalPointsRedeemed,
      icon: "pi pi-shopping-cart",
      className: "Redeemed",
    },
    {
      label: "Expired Points",
      value: metrics.totalExpiredPoints,
      icon: "pi pi-clock",
      className: "Expired",
    },
  ];

  return (
    <div className="rewards-admin-dashboard">
      <Toast ref={toast} position="top-right" />
      <div className="layout-content">
        {/* Page Header */}
        <div className="rewardsHeaderWrapper">
          <div className="rewardsHeaderIcon">
            <i className="pi pi-gift"></i>
          </div>

          <div>
            <h1 className="rewardsHeaderTitle">
                Awareness Reward summary
            </h1>

            <p className="rewardsHeaderSubtitle">
              Monitor reward points, referrals and transactions
            </p>
          </div>
        </div>

        {/* Date Range Filter */}
        <div className="rewardsDateFilterSection">
          <div className="rewardsDateFilterRow">
            <label className="font-semibold">Date Range:</label>
            <Calendar
              ref={calendarRef}
              value={dateRange}
              onChange={onDateRangeChange}
              selectionMode="range"
              readOnlyInput
              placeholder="Select Date Range"
              dateFormat="dd M yy"
              showIcon
              className="rewardsDateRangePicker"
              panelClassName="rewards-calendar-panel"
            />
          </div>
        </div>

        {/* Metric Cards */}
        <div className="rewardsMetricsGrid">
          {metricsLoading ? (
            <div className="rewardsLoadingContainer">
              <ProgressSpinner
                style={{ width: "40px", height: "40px" }}
                strokeWidth="4"
              />
            </div>
          ) : (
            metricCards.map((card, index) => (
              <div key={index} className={`rewardsMetricCard rewardsMetric${card.className}`}>
                <div className="rewardsMetricContent">
                  <div className="rewardsMetricIcon">
                    <i className={card.icon}></i>
                  </div>
                  <div className="rewardsMetricInfo">
                    <span className="rewardsMetricLabel">{card.label}</span>
                    {rewardToCurrencyRatio > 0 ? (
                      <>
                        <span className="rewardsMetricValue text-green" style={{ color: '#16a34a' }}>
                          {Helpers.formatAmountInINR(Math.round(card.value * rewardToCurrencyRatio))}
                        </span>
                        <span style={{ fontSize: '0.85rem', fontWeight: 'bold', color: '#6b7280', marginTop: '2px' }}>
                          {formatNumber(card.value)} pts
                        </span>
                      </>
                    ) : (
                      <span className="rewardsMetricValue">{formatNumber(card.value)} pts</span>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Summary Table */}
        <div className="rewardsSummarySection">
          <div className="rewardsSummaryHeader">
            <h3>User Reward Summary</h3>
            <div className="rewardsSearchContainer">
              <InputText
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onKeyDown={onSearchKeyDown}
                placeholder="Search by name or phone..."
                className="rewardsSearchInput"
              />
              <Button
                icon="pi pi-search"
                className="p-button-sm"
                onClick={onSearch}
                tooltip="Search"
                tooltipOptions={{ position: "top" }}
              />
              {searchText && (
                <Button
                  icon="pi pi-times"
                  className="p-button-sm p-button-secondary p-button-outlined"
                  onClick={onSearchClear}
                  tooltip="Clear"
                  tooltipOptions={{ position: "top" }}
                />
              )}
            </div>
          </div>

          <DataTable
            value={summaryData}
            paginator
            rows={pageSize}
            rowsPerPageOptions={[5, 10, 25, 50]}
            totalRecords={totalRecords}
            lazy={true}
            paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
            currentPageReportTemplate={`{first} to {last} of ${totalRecords}`}
            first={first}
            onPage={onPageChange}
            sortField={sortField}
            sortOrder={sortOrder}
            onSort={onSort}
            loading={summaryLoading}
            emptyMessage="No records found."
            className="rewardsSummaryTable"
            responsiveLayout="scroll"
          >
            <Column
              field="userName"
              header="User Name"
              body={userNameTemplate}
              sortable
              style={{ minWidth: "150px" }}
            />
            <Column
              field="phone"
              header="Phone"
              style={{ minWidth: "120px" }}
            />
            <Column
              field="referralCount"
              header="Referrals"
              body={referralCountTemplate}
              sortable
              style={{ minWidth: "100px" }}
            />
            <Column
              field="availablePoints"
              header="Available Points"
              body={pointsTemplate("availablePoints")}
              sortable
              style={{ minWidth: "130px" }}
            />
            <Column
              field="totalEarnedPoints"
              header="Total Earned"
              body={pointsTemplate("totalEarnedPoints")}
              sortable
              style={{ minWidth: "120px" }}
            />
            <Column
              field="totalRedeemedPoints"
              header="Total Redeemed"
              body={pointsTemplate("totalRedeemedPoints")}
              sortable
              style={{ minWidth: "130px" }}
            />
            <Column
              field="expiredPoints"
              header="Expired"
              body={pointsTemplate("expiredPoints")}
              sortable
              style={{ minWidth: "100px" }}
            />
            <Column
              header="Actions"
              body={actionsTemplate}
              style={{ minWidth: "80px", textAlign: "left" }}
            />
          </DataTable>
        </div>

        {/* Referral Dialog */}
        <Dialog
          visible={showReferralDialog}
          onHide={closeReferralDialog}
          header={`Referral tree - ${selectedUserName}`}
          modal
          style={{ width: "min(1100px, 96vw)" }}
          className="rewardsReferralDialog"
        >
          {referralLoading ? (
            <div className="rewardsDialogLoading">
              <ProgressSpinner
                style={{ width: "40px", height: "40px" }}
                strokeWidth="4"
              />
            </div>
          ) : (
            <div className="rewardReferralTreePanel">
              <div className="rewardReferralSummary">
                <div>
                  <span className="rewardReferralSummaryLabel">Referred by</span>
                  <strong>{referrerDetails?.name || "No referrer found"}</strong>
                </div>
                <div>
                  <span className="rewardReferralSummaryLabel">Referrals made</span>
                  <strong>{selectedReferralUser?.referralCount || referrals.length}</strong>
                </div>
              </div>

              <div className="rewardReferralTreeToolbar">
                <div className="rewardReferralTreeToolbarInfo">
                  <i className="pi pi-sitemap" />
                  <span>{referrals.length} direct referrals</span>
                </div>
                <div className="rewardReferralTreeControls" aria-label="Referral tree zoom controls">
                  <Button
                    icon="pi pi-minus"
                    className="p-button-rounded p-button-text p-button-sm"
                    onClick={() => updateReferralTreeZoom(referralTreeZoom - REFERRAL_TREE_ZOOM_STEP)}
                    disabled={referralTreeZoom <= REFERRAL_TREE_MIN_ZOOM}
                    tooltip="Zoom out"
                    tooltipOptions={{ position: "top" }}
                  />
                  <input
                    className="rewardReferralZoomSlider"
                    type="range"
                    min={REFERRAL_TREE_MIN_ZOOM}
                    max={REFERRAL_TREE_MAX_ZOOM}
                    step={5}
                    value={referralTreeZoom}
                    onChange={(event) => updateReferralTreeZoom(Number(event.target.value))}
                    aria-label="Referral tree zoom"
                  />
                  <Button
                    icon="pi pi-plus"
                    className="p-button-rounded p-button-text p-button-sm"
                    onClick={() => updateReferralTreeZoom(referralTreeZoom + REFERRAL_TREE_ZOOM_STEP)}
                    disabled={referralTreeZoom >= REFERRAL_TREE_MAX_ZOOM}
                    tooltip="Zoom in"
                    tooltipOptions={{ position: "top" }}
                  />
                  <span className="rewardReferralZoomValue">{referralTreeZoom}%</span>
                  <Button
                    icon="pi pi-window-maximize"
                    className="p-button-rounded p-button-text p-button-sm"
                    onClick={() => updateReferralTreeZoom(getFittedReferralZoom(referrals.length))}
                    tooltip="Fit tree"
                    tooltipOptions={{ position: "top" }}
                  />
                  <Button
                    icon="pi pi-refresh"
                    className="p-button-rounded p-button-text p-button-sm"
                    onClick={() => updateReferralTreeZoom(REFERRAL_TREE_DEFAULT_ZOOM)}
                    tooltip="Reset zoom"
                    tooltipOptions={{ position: "top" }}
                  />
                </div>
              </div>

              <div
                ref={referralChartScrollerRef}
                className="rewardOrgChartScroller"
                onWheel={handleReferralTreeWheel}
              >
                <div
                  className="rewardOrgChartZoomLayer"
                  style={{ "--referral-tree-zoom": referralTreeZoom / 100 } as React.CSSProperties}
                >
                  <div className="rewardLayeredTree">
                    {referrerDetails && (
                      <>
                        <div className="rewardTreeLevel rewardTreeLevelSingle rewardTreeLevelReferrer">
                          <div className="rewardOrgNode rewardOrgNodeReferrer">
                            {renderReferralPersonCard(referrerDetails)}
                          </div>
                        </div>
                        <div className="rewardTreeConnector rewardTreeConnectorSingle" />
                      </>
                    )}

                    {selectedReferralPerson && (
                      <div className="rewardTreeLevel rewardTreeLevelSingle rewardTreeLevelSelected">
                        <div className="rewardOrgNode rewardOrgNodeSelected">
                          {renderReferralPersonCard(selectedReferralPerson)}
                        </div>
                      </div>
                    )}

                    {referrals.length > 0 && (
                      <>
                        <div className="rewardTreeConnector rewardTreeConnectorFan" />
                        <div className="rewardTreeChildrenGrid">
                          {referrals.map((referral, index) => (
                            <div
                              key={`${referral.userId || referral.phone || referral.userName}-${index}`}
                              className="rewardOrgNode rewardOrgNodeReferred rewardTreeChildNode"
                            >
                              {renderReferralPersonCard({
                                userId: referral.userId,
                                name: referral.userName,
                                phone: referral.phone,
                                role: "referred",
                                joinedDate: referral.joinedDate,
                              })}
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {referrals.length > 0 && (
                <DataTable
                  value={referrals}
                  responsiveLayout="scroll"
                  emptyMessage="No referrals found."
                  className="rewardReferralList"
                >
                  <Column field="userName" header="Name" />
                  <Column field="phone" header="Phone" />
                  <Column
                    field="joinedDate"
                    header="Joined Date"
                    body={referralDateTemplate}
                  />
                </DataTable>
              )}
            </div>
          )}
        </Dialog>

        {/* Transaction Dialog */}
        <TransactionDialog
          visible={showTransactionDialog}
          onHide={() => {
            setShowTransactionDialog(false);
            setTransactionFirst(0);
            setTransactionPageNumber(1);
            setTxSearchInput("");
            setTxSearchQuery(null);
          }}
          userName={transactionUserName}
          transactions={transactions}
          isLoading={transactionLoading}
          totalRecords={transactionTotalRecords}
          first={transactionFirst}
          rows={transactionPageSize}
          onPageChange={onTransactionPageChange}
          rowsPerPageOptions={[5, 10, 25, 50]}
          searchInput={txSearchInput}
          searchQuery={txSearchQuery}
          onSearchInputChange={setTxSearchInput}
          onSearch={onTxSearch}
          onSearchClear={onTxSearchClear}
          sortOptions={transactionSortOptions}
          selectedSortOption={selectedTxSortOption}
          onSortChange={onTransactionSortChange}
          formatNumber={formatNumber}
          formatDate={formatDate}
          formatDateTime={formatDateTime}
        />

      </div>
    </div>
  );
};

export default RewardsAdminDashboard;